import { useState, useEffect, useCallback } from 'react';
import { useResizeObserver } from '@cognite/cogs-utils';

// How this works:
// - scrollableParent: a parent element that can overflow
// - rightMostElement: a child element that is the right most in
//   scrollableParent i.e. which is the one that goes out of the parent's
//   boundaries. This could either be elementToTruncate itself, or if there are
//   further elements to the right of elementToTruncate, it can be it's
//   parent/grandparent that is the right-most child of scrollableParent
// - elementToTruncate: A truncatable element which should be shrunk via
//   maxWidth so that the overall width of all the children inside
//   scrollableParent remains equal to it's own width and overflowing doesn't
//   occur
// - 1. we take original widths of rightMostElement and elementToTruncate on
//      first render
// - 2. we then calculate how much of rightMostElement's right coordinate exceeds
//      out of scrollableParent's right coordinate. this can happen anytime
//      scrollableParent's width changes either via editing it's children or
//      viewport resizing
// - 3. we then subtract this offset from the original width of
//      elementToTruncate to get our maxWidth that we can use on the element
// ---- so in the end we shrink the elementToTruncate by how much the
//      rightMostElement went outside the scrollableParent
var useResponsiveMaxWidth = function useResponsiveMaxWidth(scrollableParentRef, rightMostElementRef, elementToTruncateRef) {
  var _useState = useState(undefined),
    rightMostElementOriginalWidth = _useState[0],
    setRightMostElementOriginalWidth = _useState[1];
  var _useState2 = useState(undefined),
    elementToTruncateOriginalWidth = _useState2[0],
    setElementToTruncateOriginalWidth = _useState2[1];
  var _useState3 = useState(undefined),
    elementToTruncateMaxWidth = _useState3[0],
    setElementToTruncateMaxWidth = _useState3[1];
  // to trigger offset calculation every time the scrollable parent changes size
  var scrollableParentSize = useResizeObserver(scrollableParentRef);
  // calculates the original width from which the offset is subtracted later
  useEffect(function () {
    var _a, _b;
    setRightMostElementOriginalWidth((_a = rightMostElementRef.current) === null || _a === undefined ? undefined : _a.clientWidth);
    setElementToTruncateOriginalWidth((_b = elementToTruncateRef.current) === null || _b === undefined ? undefined : _b.clientWidth);
  }, [elementToTruncateRef, rightMostElementRef]);
  // calculates the offset by measuring how much the right most element is
  // outside the bounds of the parent container
  var calculateOffset = useCallback(function () {
    if (!scrollableParentRef.current || !rightMostElementRef.current || !rightMostElementOriginalWidth) return undefined;
    var scrollableParentRightBoundary = scrollableParentRef.current.getBoundingClientRect().left + scrollableParentRef.current.clientWidth;
    var rightMostElementOriginalRightBoundary = rightMostElementRef.current.getBoundingClientRect().left + rightMostElementOriginalWidth;
    if (rightMostElementOriginalRightBoundary > scrollableParentRightBoundary) return rightMostElementOriginalRightBoundary - scrollableParentRightBoundary;
    return undefined;
  }, [scrollableParentRef, rightMostElementOriginalWidth, rightMostElementRef]);
  // calculates max width by subtracting offset from the original width that was
  // calculated after first render
  useEffect(function () {
    var offset = calculateOffset();
    if (elementToTruncateOriginalWidth && offset) setElementToTruncateMaxWidth(elementToTruncateOriginalWidth - offset + "px");else setElementToTruncateMaxWidth(undefined);
  }, [scrollableParentSize, elementToTruncateOriginalWidth, rightMostElementOriginalWidth, rightMostElementRef, calculateOffset]);
  return elementToTruncateMaxWidth;
};

export { useResponsiveMaxWidth };
